import type { Addin, AddinCommand } from "owa-addins-store";
import type Control from "owa-service/lib/contract/Control";
import type { AddInExtensionControlButton } from "owa-graph-schema";

/**
 * Pure add-in command helpers (supertip text, command counting) with no runtime dependencies.
 *
 * These functions were split out of `AddinChecker.ts` on purpose: `AddinChecker` value-imports
 * `getExtensibilityState` from `owa-addins-store`, so importing anything from it drags the entire
 * `owa-addins-store` runtime (~1.2MB) into the consuming bundle. The helpers below only need types,
 * which are erased at build time, so consumers that import from this module pull in no extra
 * runtime code. Keep this file free of value imports.
 */

export function isSingleCommand(addin: Addin): boolean {
    return addin.AddinCommands.size == 1;
}

export function getFirstCommand(addin: Addin): AddinCommand {
    if (addin.AddinCommands.size > 0) {
        return [...addin.AddinCommands.values()][0] as AddinCommand;
    }
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322: Type 'null' is not assignable to type 'AddinCommand'.
    // @ts-expect-error
    return null;
}

export function needShowMenu(addin: Addin): boolean {
    if (isSingleCommand(addin)) {
        return getFirstCommand(addin).isMenu;
    }
    else {
        return addin.AddinCommands.size > 1;
    }
}

export function canExecute(addin: Addin): boolean {
    if (!isSingleCommand(addin)) {
        return false;
    }
    else {
        return getFirstCommand(addin).isActionable;
    }
}

export function getSupertipTitle(addin: Addin): string {
    if (isSingleCommand(addin)) {
        const firstCommand = getFirstCommand(addin);
        // Supertip is required in manifest, it won't be null or empty
        return getCommandSupertipTitle(firstCommand);
    }
    return "";
}

export function getCommandSupertipTitle(addinCommand: AddinCommand): string {
    const control = addinCommand?.control as Control;
    const supertipTitle = control?.Supertip
        ? control.Supertip.Title
        : (control as AddInExtensionControlButton)?.supertip?.title;
    return (supertipTitle as string) || "";
}

// XML manifests expose the control supertip as `Supertip.Description`; MetaOS/JSON manifests
// expose it as `supertip.description`. Read whichever is present so the control-level supertip
// description reaches parity across both manifest formats.
export function getCommandSupertipDescription(addinCommand: AddinCommand): string {
    const control = addinCommand?.control as Control;
    const supertipDescription = control?.Supertip
        ? control.Supertip.Description
        : (control as AddInExtensionControlButton)?.supertip?.description;
    return (supertipDescription as string) || "";
}

export function getSupertipDescription(addin: Addin): string {
    if (isSingleCommand(addin)) {
        return getCommandSupertipDescription(getFirstCommand(addin));
    }
    return "";
}

export function getAccessibilityText(addin: Addin): string {
    let accessibilityText = addin.DisplayName || "";
    if (isSingleCommand(addin)) {
        const superTipTitle = getSupertipTitle(addin);
        const supertipDescription = getSupertipDescription(addin);
        accessibilityText = supertipDescription
            ? superTipTitle + " - " + supertipDescription
            : superTipTitle || "";
    }
    return accessibilityText;
}
